/**
 * TOSS Web Application — how an internal notification reads, in one place
 *
 * Split out of `NotificationItem` on 2026-08-26, when the header panel moved onto the shared
 * popover (`components/topbar/NotificationsPopover.tsx`) and that component was left with no
 * caller. What the panel still needed from it is here: where a notification LEADS, and the words
 * a row shows besides its title.
 *
 * The mirror of `hooks/api/clientPortal/clientNotificationPresentation.ts`, deliberately —
 * the two audiences have separate destinations (a dispatcher's application card is not the
 * customer's) and separate dictionaries, and the shared panel is given the RESULT of both.
 */

import {
  applicationDetailsPath,
  taskDetailsPath,
  expenseDetailsPath,
  messengerChatPath,
} from '@/constants';
import { NotificationTypeLabels, type Notification } from '@/types';
import { formatRelativeAge } from '@/utils/date';

/**
 * Where the notification opens, or `null` when it opens nowhere.
 *
 * Order matters and is the one `NotificationItem` used: a message notification carries both a
 * chat and, often, the application it was written about — and the message is what the recipient
 * was told about.
 */
export function notificationTarget(notification: Notification): string | null {
  if (notification.chatId) return messengerChatPath(notification.chatId);
  if (notification.applicationId) return applicationDetailsPath(notification.applicationId);
  if (notification.taskId) return taskDetailsPath(notification.taskId);
  if (notification.expenseId) return expenseDetailsPath(notification.expenseId);
  return null;
}

/**
 * The row's second line: what this is about, then how old it is.
 *
 * The type label stands in when the notification has no text of its own — it is what the coloured
 * badge used to say, in the space where the badge used to be. A row that fell back to the age
 * alone would drop the only word saying whether this is an application, a task or an expense.
 */
export function notificationMeta(notification: Notification): string {
  const age = formatRelativeAge(notification.createdAt);
  const subject = notification.content?.trim() || NotificationTypeLabels[notification.type];
  return subject ? `${subject} · ${age}` : age;
}
